import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { API_BASE_URL, getAuthHeader } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { GoogleLogin } from '../components/GoogleLogin'
import { AppleLogin } from '../components/AppleLogin'
import type { AppleLoginPayload } from '../lib/authTypes'
import type { ToastMessage, ViewMode } from '../types'
import { LoadingAnimation } from '../components/ui/LoadingAnimation'
import { CockpitRuntime } from '../components/cockpit/CockpitRuntime'
import { compileViews } from '../lib/cockpit/compiler/viewCompiler'
import { DEFAULT_PERSONAS, type CIG } from '../lib/cockpit/schemas'
import { normalizeLegacyMetrics } from '../lib/cockpit/normalizers'
import {
  dispatchPodcastPlayback,
  getPodcastActionState,
  resolveIntelligencePodcastPlayback,
} from '../lib/podcastPlayback'

interface ReportViewPageProps {
  reportId: string
  isDarkMode: boolean
  onShowToast: (toast: Pick<ToastMessage, 'type' | 'title' | 'description'>) => void
  viewMode: ViewMode
  hasFloatingAudio?: boolean
}

interface ReportData {
  research: any
}

export default function ReportViewPage({
  reportId,
  isDarkMode,
  onShowToast,
  viewMode,
  hasFloatingAudio = false,
}: ReportViewPageProps) {
  const { user, loginWithGoogle, loginWithApple } = useAuth()
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSignupPrompt, setShowSignupPrompt] = useState(false)
  const [needsAuth, setNeedsAuth] = useState(false)

  const navigate = (path: string) => { window.location.href = path }

  const completeLogin = async (loginAction: () => Promise<void>, successTitle = 'Welcome!') => {
    try {
      await loginAction()
      onShowToast({ type: 'success', title: successTitle, description: 'You have been successfully logged in.' })
      setNeedsAuth(false)
      setShowSignupPrompt(false)
    } catch (err) {
      onShowToast({ type: 'error', title: 'Login failed', description: err instanceof Error ? err.message : 'Please try again.' })
    }
  }

  const handleGoogleLogin = async (credential: string) => {
    await completeLogin(() => loginWithGoogle(credential), 'Welcome!')
  }

  const handleAppleLogin = async (payload: AppleLoginPayload) => {
    await completeLogin(() => loginWithApple(payload), 'Welcome!')
  }

  useEffect(() => {
    const loadReport = async () => {
      setIsLoading(true)
      setError(null)
      setNeedsAuth(false)
      try {
        const response = await fetch(`${API_BASE_URL}/report/${reportId}`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...getAuthHeader(),
          },
        })
        if (response.status === 403) {
          if (user) throw new Error('You do not have permission to view this report.')
          setNeedsAuth(true)
          return
        }
        if (!response.ok) {
          throw new Error('Report not found or unavailable.')
        }
        const data = await response.json()
        if (!data || !data.research) {
          throw new Error('Invalid report data.')
        }
        setReportData(data)
        if (data.research.title) {
          document.title = `${data.research.title} - InsightAtlas`
        }
      } catch (err) {
        console.error('Failed to load report:', err)
        setError(err instanceof Error ? err.message : 'Failed to load report.')
      } finally {
        setIsLoading(false)
      }
    }
    loadReport()
  }, [reportId, user])

  const isIntelligenceReport = !!(reportData?.research?.report_type === 'intelligence' && reportData.research.cig && reportData.research.sib)
  const updatePodcastState = useCallback((podcastInfo: any) => {
    setReportData((prev) => {
      if (!prev?.research) return prev
      return {
        ...prev,
        research: {
          ...prev.research,
          podcast: podcastInfo,
          podcast_status: (podcastInfo.audio_url ? 'ready' : podcastInfo.status) || prev.research.podcast_status || 'missing',
          podcast_audio_ready: Boolean(podcastInfo.audio_url),
          podcast_duration_seconds: podcastInfo.duration_seconds ?? prev.research.podcast_duration_seconds,
          podcast_error_message: podcastInfo.error_message ?? undefined,
        },
      }
    })
  }, [])

  const podcastAction = reportData?.research
    ? getPodcastActionState(
        {
          ...reportData.research,
          id: String(reportData.research.research_id || reportData.research.id || reportId),
          is_intelligence_report: true,
          intelligence_report_id: reportId,
        },
      )
    : null

  const handleListen = useCallback(async () => {
    try {
      const research = reportData?.research
      if (!research || !reportId) {
        onShowToast({
          type: 'warning',
          title: 'Podcast unavailable',
          description: 'This report is still syncing its playback metadata.',
        })
        return
      }
      if (podcastAction?.disabled) {
        return
      }

      let pendingToastShown = false
      const podcastInfo = await resolveIntelligencePodcastPlayback(reportId, {
        onPending: (pending) => {
          updatePodcastState(pending)
          if (pendingToastShown) return
          pendingToastShown = true
          onShowToast({
            type: 'info',
            title: 'Preparing Podcast',
            description: 'Podcast generation has started. Playback will begin as soon as audio is ready.',
          })
        },
      })
      updatePodcastState(podcastInfo)

      if (!podcastInfo.audio_url) {
        onShowToast({
          type: podcastInfo.status === 'failed' ? 'error' : 'warning',
          title: podcastInfo.status === 'failed' ? 'Podcast failed' : 'Podcast still processing',
          description:
            podcastInfo.error_message ||
            'The podcast is still being generated. Please try again in a moment.',
        })
        return
      }

      dispatchPodcastPlayback(
        {
          id: String(research.research_id || research.id || reportId),
          title: research.title || research.query || 'Intelligence Report',
          description: research.query || research.title || 'Intelligence report',
          summary: research.summary || '',
          category: research.category || 'General',
          trending_percentage: 0,
          engagement_count: 0,
          sources: research.sources || [],
          created_at: research.created_at || new Date().toISOString(),
          tags: [],
          image_url: research.image_url,
          is_intelligence_report: true,
          intelligence_report_id: reportId,
        },
        podcastInfo
      )
    } catch (error) {
      console.error('Failed to start report podcast playback:', error)
      onShowToast({
        type: 'error',
        title: 'Podcast failed',
        description: error instanceof Error ? error.message : 'Unable to load the podcast right now.',
      })
    }
  }, [onShowToast, podcastAction?.disabled, reportData, reportId, updatePodcastState])

  const cockpitData = useMemo(() => {
    if (!isIntelligenceReport || !reportData) return null
    try {
      const research = reportData.research
      const cig = research.cig || {}
      const sib = research.sib || {}
      const scenarioPackage = research.scenario
      const cigMetadata = (cig as any).metadata || {}

      const scenarioAxes = (() => {
        if (!scenarioPackage?.axis) return (cig as any).scenario_axes ?? []
        const axis = scenarioPackage.axis
        return [{
          axis_id: axis.axis_id || 'default',
          label: axis.label || 'Scenario Analysis',
          description: axis.description,
          states: (axis.states || []).map((state: any) => ({
            state_id: state.state_id,
            label: state.label,
            short_description: state.short_description || state.description,
            is_default: state.is_default,
            probability_band: state.probability_band,
            triggers: (state.triggers || []).map((t: any) => ({ trigger_id: t.trigger_id || t.id, description: t.description, observability: t.observability, confidence: t.confidence })),
            assumptions: (state.assumptions || []).map((a: any) => ({ assumption_id: a.assumption_id || a.id, text: a.text, rationale: a.rationale })),
            metric_impacts: state.metric_impacts || [],
          })),
          triggers: (scenarioPackage.global_triggers || axis.triggers || []).map((t: any) => ({ id: t.trigger_id || t.id, label: t.label || t.description, definition: t.definition || t.description, indicator_type: t.indicator_type, maps_to_state_transitions: t.maps_to_state_transitions, confidence: t.confidence })),
          assumptions: (axis.assumptions || []).map((a: any) => ({ id: a.assumption_id || a.id, text: a.text, applies_to_state_id: a.applies_to_state_id })),
        }]
      })()

      const sibTitle = sib?.research_title
      const cigTitle = cigMetadata.title
      const userQuery = research.title || research.query || ''
      let bestTitle = userQuery
      if (sibTitle && sibTitle.length > 10 && sibTitle !== userQuery) bestTitle = sibTitle
      else if (cigTitle && cigTitle.length > 10 && cigTitle !== userQuery) bestTitle = cigTitle

      const enhancedCig: CIG = {
        ...cig,
        metadata: {
          ...cigMetadata,
          schema_version: cigMetadata.schema_version || '2.2',
          report_id: cigMetadata.report_id || research.intelligence_report_id || reportId,
          title: bestTitle,
        },
        sources: (cig as any).sources ?? [],
        claims: (cig as any).claims ?? [],
        entities: (cig as any).entities ?? [],
        metrics: normalizeLegacyMetrics((cig as any).metrics as any),
        relationships: (cig as any).relationships ?? [],
        figures: (cig as any).figures ?? [],
        datasets: (cig as any).datasets ?? [],
        watchlist: (cig as any).watchlist ?? [],
        key_findings: (cig as any).key_findings ?? [],
        executive_summary: (cig as any).executive_summary ?? [],
        bluf: ((cig as any).bluf ?? research.title) || research.query,
        report_sections: (cig as any).report_sections ?? [],
        scenario_axes: scenarioAxes.length > 0 ? scenarioAxes : (cig as any).scenario_axes,
      }

      const persona = DEFAULT_PERSONAS.find(p => p.persona_id === 'general_exec') || DEFAULT_PERSONAS[0]

      const deepResearch = research.deep_research ? {
        text: research.deep_research.text || research.deep_research_report || research.written_report || research.report_text || '',
        sources: research.deep_research.sources || [],
        citations: research.deep_research.citations || [],
        status: research.deep_research.status,
        provider: research.deep_research.provider,
      } : (research.written_report || research.deep_research_report || research.report_text) ? {
        text: research.written_report || research.deep_research_report || research.report_text || '',
      } : undefined

      const views = compileViews(enhancedCig, persona, sib, deepResearch)

      return {
        enhancedCig,
        views,
        persona,
        heroImageUrl: research.image_url,
        studyMeta: {
          id: `ia-${reportId}`,
          intelligenceReportId: reportId,
          researchId: research.id,
          isSaved: false,
          isPublic: research.is_public ?? true,
          category: research.category,
          createdAt: research.created_at,
          query: research.query || research.title,
          researcher: research.author?.name,
          researcherEmail: research.author?.email,
        },
      }
    } catch (e) {
      console.error('Failed to compile cockpit data:', e)
      return null
    }
  }, [reportData, isIntelligenceReport, reportId])

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="flex justify-center mb-4"><LoadingAnimation size={64} /></div>
          <p className="text-gray-600 dark:text-gray-400">Loading report...</p>
        </div>
      </div>
    )
  }

  // Private report — needs authentication
  if (needsAuth && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-4">
        <div className="bg-white/[0.03] backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4">
              <img src="/logo192.png" alt="Insight Atlas" className="w-full h-full rounded-2xl shadow-lg" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Private Report</h2>
            <p className="text-white/50 text-sm">Sign in to view this intelligence report.</p>
          </div>
          <div className="space-y-3">
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={(error) => onShowToast({ type: 'error', title: 'Sign in failed', description: error })}
            />
            <AppleLogin
              onSuccess={handleAppleLogin}
              onError={(error) => onShowToast({ type: 'error', title: 'Sign in failed', description: error })}
            />
          </div>
          <div className="mt-6 text-center">
            <button onClick={() => navigate('/')} className="text-white/40 hover:text-white/60 text-sm transition-colors">
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Unable to Load Report</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-medium transition-colors">
            Go to Homepage
          </button>
        </div>
      </div>
    )
  }

  if (!reportData) return null

  // Full CockpitRuntime view for intelligence reports
  if (isIntelligenceReport && cockpitData) {
    return (
      <>
        {!user && (
          <div className="bg-blue-600 text-white py-3 px-4 text-center relative z-50">
            <p className="text-sm">
              Sign up to save this article and create your own AI-powered research
              <button onClick={() => setShowSignupPrompt(true)} className="ml-3 bg-white text-blue-600 px-4 py-1 rounded-full text-sm font-medium hover:bg-blue-50 transition-colors">
                Sign Up Free
              </button>
            </p>
          </div>
        )}
        <div className="bg-gray-50 dark:bg-gray-900" style={{ height: !user ? 'calc(100dvh - 44px)' : '100dvh' }}>
          <div className="h-full overflow-y-auto">
            <CockpitRuntime
              cig={cockpitData.enhancedCig}
              views={cockpitData.views}
              persona={cockpitData.persona}
              onBack={() => navigate('/')}
              onListen={podcastAction?.visible ? handleListen : undefined}
              listenLabel={podcastAction?.label}
              listenDisabled={podcastAction?.disabled}
              listenLoading={podcastAction?.showSpinner}
              viewMode={viewMode === 'mobile' ? 'mobile' : 'desktop'}
              hasFloatingAudio={hasFloatingAudio}
              studyMeta={cockpitData.studyMeta}
              heroImageUrl={cockpitData.heroImageUrl}
            />
          </div>
        </div>

        {showSignupPrompt && !user && (
          <>
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100] transition-all duration-300" onClick={() => setShowSignupPrompt(false)} />
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl sm:rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 transform transition-all duration-500 relative mx-4" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setShowSignupPrompt(false)} className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <svg className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Enjoyed this research?</h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Create your free account to unlock powerful AI research tools</p>
                </div>
                <div className="w-full space-y-3">
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={(error) => onShowToast({ type: 'error', title: 'Sign up failed', description: error })}
                  />
                  <AppleLogin
                    onSuccess={handleAppleLogin}
                    onError={(error) => onShowToast({ type: 'error', title: 'Sign up failed', description: error })}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center px-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Report Unavailable</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">This report format is no longer supported.</p>
      </div>
    </div>
  )
}
